import React, { useState, useLayoutEffect } from "react";
import { Plus, Calendar, Filter, TrendingUp, DollarSign, X, ChevronDown, Home, Zap, Users, Wrench, Globe, Receipt, List, UtensilsCrossed } from "lucide-react";
import "../../../styles/stores/expenses/expensesNew.css";
import { refreshUserToken } from "../../../tools/refreshToken";
import { addExpensesUrl, getStoreExpensesUrl, getStoreExpensebyMonthUrl } from "../../../api/endpoints";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Spinner, { ButtonLoader } from "../../../components/uxCollections/Loaders";
import ExpenseLoadingSkeleton from "./ExpensesLoaderSkeleton";
import { uiAlert } from "../../../components/alerts/customAlert/uiAlert";
import CategoryExpensesOverlay from "./ExpenseDetailOverlay";
const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ExpenseTracker = () => {
  const [isLoading, setIsLoading] = useState(true); //
  const [selectedMonth, setSelectedMonth] = useState(monthNames[new Date().getMonth()]);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenses, setExpenses] = useState({});
  const [expenseDescription, setExpenseDescription] = useState("");
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  const { store } = useSelector((state) => state.store);
  const { user } = useSelector((state) => state.webStoreUser);
  const currency = user.userCountryCurrencySymbol || "$";
  const navigate = useNavigate();

  useLayoutEffect(() => {
    fetchExpenses();
  }, []);

  const expenseCategories = [
    {
      id: "Marketing",
      name: "Marketing",
      description: "Campaign for sales, posters",
      icon: TrendingUp,
      color: "expense-marketing",
    },
    {
      id: "Rent",
      name: "Rent",
      description: "Shop rent, store rent, office rent",
      icon: Home,
      color: "expense-rent",
    },
    {
      id: "Salary",
      name: "Salary",
      description: "Salary for staff, workers, employees",
      icon: Users,
      color: "expense-salary",
    },
    {
      id: "Bills",
      name: "Bills",
      description: "Bill for electricity, water, internet, p...",
      icon: Zap,
      color: "expense-bills",
    },
    {
      id: "Repairs",
      name: "Repairs",
      description: "Repair of shop, machines etc",
      icon: Wrench,
      color: "expense-repairs",
    },
    {
      id: "OnlineAds",
      name: "Online Ads",
      description: "Online Ads created on storelense",
      icon: Globe,
      color: "expense-ads",
    },
    {
      id: "Tax",
      name: "Tax",
      description: "Community, government tax",
      icon: Receipt,
      color: "expense-tax",
    },
    //foo
    {
      id: "Food",
      name: "Food",
      description: "Food expenses for staff and customers",
      icon: UtensilsCrossed,
      color: "expense-food",
    },
    {
      id: "Other",
      name: "Others",
      description: "Every other expenses not listed",
      icon: List,
      color: "expense-others",
    },
  ];

  const totalExpenses = Object.values(expenses).reduce((sum, amount) => sum + amount.value, 0);

  const handleAddExpense = () => {
    if (selectedCategory && expenseAmount) {
      addExpenses();
      setSelectedCategory("");
      setExpenseAmount("");
      setShowAddExpense(false);
    }
  };
  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      let response = await fetch(getStoreExpensesUrl + store._id, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (response.ok) {
        let expenses = await response.json();

        setExpenses(expenses.data);
      } else if (response.status === 400) {
        refreshUserToken(getStoreExpensesUrl + store._id, fetchExpenses, navigate);
      } else {
        uiAlert("An error occured while fetching expenses", "error");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* month 0-11 0 for jan */
  const getExpensesByMonth = async (month) => {
    setIsLoading(true);
    try {
      let response = await fetch(getStoreExpensebyMonthUrl + store._id + "/" + month, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch expenses for the selected month");
      }

      if (response.ok) {
        const expenses = await response.json();

        setExpenses(expenses.data); // change to the month in focus
      }
    } catch (error) {
      console.log(error);
      return uiAlert("An error occured while fetching expenses for the selected month", "error");
    } finally {
      setIsLoading(false);
    }
  };
  const addExpenses = async () => {
    setIsLoading(true);
    try {
      let payload = {
        month: monthNames[new Date().getMonth()],
        category: selectedCategory.toLowerCase(),
        amount: parseInt(expenseAmount),
        description: expenseDescription,
        name: selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1) + " expense for " + monthNames[new Date().getMonth()],
        addedBy: "Admin",
      };
      let response = await fetch(addExpensesUrl + store._id, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        let expense = await response.json();

        setExpenses(expense.updatedMonth);

        uiAlert("Expense added successfully", "success");
        setShowAddExpense(false);
        setSelectedMonth(monthNames[new Date().getMonth()]);
      }
    } catch (error) {
      console.log(error);
      return uiAlert("An error occured while adding expense", "error");
    } finally {
      setIsLoading(false);
      setExpenseDescription("");
    }
  };

  const ExpenseCard = ({ category, amount, icon: Icon, description, color, handleClick }) => (
    <div onClick={handleClick} className={`expense-card ${color}`}>
      <div className='expense-card-content'>
        <div className='expense-card-header'>
          <div className='expense-icon'>
            <Icon size={18} />
          </div>
          <div className='expense-info'>
            <h3 className='expense-category'>{category}</h3>
            <p className='expense-description'>{description}</p>
          </div>
        </div>
        <div className='expense-amount'>
          {currency} {amount.toLocaleString()}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isLoading ? (
        <ExpenseLoadingSkeleton />
      ) : (
        <div className='expense-dashboard'>
          <div className='expense-container'>
            <div className='expense-header'>
              <div className='expense-header-content'>
                <div className='expense-title-section'>
                  <div className='expense-title-info'>
                    <h1 className='expense-main-title'>Showing expenses for current year</h1>
                    <p className='expense-subtitle'>Track and manage your store expenses</p>
                  </div>
                </div>

                <div className='expense-controls'>
                  <div className='month-selector'>
                    <button className='month-button' onClick={() => setShowMonthDropdown(!showMonthDropdown)}>
                      <Calendar size={16} />
                      {selectedMonth}
                      <ChevronDown size={14} />
                    </button>
                    {showMonthDropdown && (
                      <div className='month-dropdown'>
                        {monthNames.map((month, index) => (
                          <div
                            key={month}
                            className={`month-option ${month === selectedMonth ? "selected" : ""}`}
                            onClick={() => {
                              getExpensesByMonth(index);
                              setSelectedMonth(month);
                              setShowMonthDropdown(false);
                            }}
                          >
                            {month}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button className='add-expense-btn' onClick={() => setShowAddExpense(true)}>
                    <Plus size={16} />
                    Add expenses
                  </button>
                </div>
              </div>
            </div>

            <div className='expenses-section'>
              <div className='expenses-grid'>
                {expenseCategories.map((category) => {
                  return (
                    <ExpenseCard
                      key={category.id}
                      category={category.name}
                      amount={expenses[category.id.toLowerCase()]?.value || 0}
                      icon={category.icon}
                      description={category.description}
                      color={category.color}
                      handleClick={() => {
                        setOpenCategory(category);
                        setSelectedExpense(expenses[category.id.toLowerCase()]?.expenses || []);
                      }}
                    />
                  );
                })}
              </div>

              <div className='total-section'>
                <div className='total-label'>Total</div>
                <div className='total-amount'>
                  {currency} {totalExpenses.toLocaleString()}
                </div>
              </div>
            </div>
            {showAddExpense && (
              <div className='modal-overlay1' onClick={() => setShowAddExpense(false)}>
                <div className='modal-content' onClick={(e) => e.stopPropagation()}>
                  <div className='modal-header'>
                    <h2 className='modal-title'>Add Expense</h2>
                    <button className='modal-close' onClick={() => setShowAddExpense(false)}>
                      <X size={16} />
                    </button>
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Category</label>
                    <select className='form-select' value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                      <option value=''>Select Category</option>
                      {expenseCategories.map((category) => (
                        <option key={category.id} value={category.id.toLowerCase()}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Amount ({currency})</label>
                    <input
                      type='number'
                      className='form-input'
                      placeholder='Enter cost'
                      value={expenseAmount}
                      onChange={(e) => setExpenseAmount(e.target.value)}
                    />
                  </div>

                  <div className='form-group'>
                    <label className='form-label'>Describe the expenses</label>
                    <textarea
                      className='form-textarea'
                      placeholder='Enter description'
                      value={expenseDescription}
                      onChange={(e) => {
                        setExpenseDescription(e.target.value);
                      }}
                    />
                  </div>

                  <div className='form-actions'>
                    <button className='btn-cancel' onClick={() => setShowAddExpense(false)}>
                      Cancel
                    </button>
                    <button
                      className='btn-add'
                      onClick={handleAddExpense}
                      disabled={!selectedCategory || !expenseAmount}
                      style={{
                        opacity: !selectedCategory || !expenseAmount ? 0.5 : 1,
                        cursor: !selectedCategory || !expenseAmount ? "not-allowed" : "pointer",
                      }}
                    >
                      Add Expense
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {selectedExpense && (
            <CategoryExpensesOverlay
              category={openCategory?.id}
              expenses={selectedExpense}
              onClose={() => {
                setSelectedExpense(null);
                setOpenCategory(null);
              }}
              currency={currency}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ExpenseTracker;
